import styled from 'styled-components';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import type { SnapshotLevel } from '../../../contentEntryRefs/contentEntryRefs';
import { NamedContentEntryText } from '../../ContentEntryText/NamedContentEntryText';
import { useIsRTLLayout } from '../../../appState/hooks/useIsRTLLayout';

export type SteppingProgressBarStep = {
  label: SnapshotLevel;
  value: number;
};

type SteppingProgressBarProps = {
  steps: SteppingProgressBarStep[] | undefined;
  value: number | undefined;
  showLabels?: boolean;
};

export const SteppingProgressBar: FC<SteppingProgressBarProps> = ({ steps, value, showLabels = true }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [showEdgeLabelsOnly, setShowEdgeLabelsOnly] = useState(false);

  const labelsAndPositions = steps?.map((step, i) => ({
    labelName: `securitySnapshotLevels${step.label}` as const,
    label: step.label,
    posPct: (100 * i) / (steps.length - 1),
  }));

  const visibleLabels = useMemo(() => {
    if (!labelsAndPositions) {
      return undefined;
    }

    if (!showEdgeLabelsOnly || labelsAndPositions.length <= 2) {
      return labelsAndPositions;
    }

    return [labelsAndPositions[0], labelsAndPositions[labelsAndPositions.length - 1]].filter(
      (label): label is NonNullable<typeof labelsAndPositions>[number] => label != null,
    );
  }, [labelsAndPositions, showEdgeLabelsOnly]);

  const { achievedStep, achievedStepThreshold } = steps?.reduce(
    (acc, step) =>
      value != null && value >= step.value
        ? { achievedStep: acc.achievedStep + 1, achievedStepThreshold: step.value }
        : acc,
    { achievedStep: -1, achievedStepThreshold: 0 },
  ) ?? { achievedStep: -1, achievedStepThreshold: 0 };
  const nextStep: SteppingProgressBarStep | undefined =
    steps && achievedStep + 1 < steps.length ? steps[achievedStep + 1] : undefined;
  const nextStepThreshold = nextStep ? nextStep.value - achievedStepThreshold : undefined;
  const nextStepProgress = value != null ? value - achievedStepThreshold : undefined;
  const nextStepPercentage =
    nextStepThreshold != null && nextStepProgress != null && steps
      ? nextStepProgress / nextStepThreshold / (steps.length - 1)
      : 0;
  const progressPercentage = steps ? (achievedStep / (steps.length - 1) + nextStepPercentage) * 100 : 0;
  const isRTL = useIsRTLLayout();

  useEffect(() => {
    if (!showLabels || !labelsAndPositions || labelsAndPositions.length <= 2 || !containerRef.current) {
      setShowEdgeLabelsOnly(false);
      return;
    }

    const evaluateOverlap = () => {
      const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 0;
      if (!containerWidth) {
        return;
      }

      const labelWidths = labelsAndPositions.map(
        (_, index) => measureRefs.current[index]?.getBoundingClientRect().width ?? 0,
      );

      let previousRight = -Infinity;
      let overlaps = false;

      labelWidths.forEach((width, index) => {
        const posPx = (containerWidth * index) / Math.max(labelsAndPositions.length - 1, 1);
        const left = index === 0 ? 0 : index === labelWidths.length - 1 ? containerWidth - width : posPx - width / 2;
        const right = left + width;

        if (left < previousRight) {
          overlaps = true;
        }

        previousRight = right;
      });

      setShowEdgeLabelsOnly(overlaps);
    };

    evaluateOverlap();

    const observer = new ResizeObserver(evaluateOverlap);
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [labelsAndPositions, showLabels]);

  return steps && labelsAndPositions && visibleLabels ? (
    <Container>
      <MeasureLabels aria-hidden="true">
        {labelsAndPositions.map(({ label, labelName }, index) => (
          <MeasureLabel
            key={`measure-${label}`}
            ref={(element) => {
              measureRefs.current[index] = element;
            }}
          >
            <NamedContentEntryText refKey={labelName} container={'span'} subKey="levelName" />
          </MeasureLabel>
        ))}
      </MeasureLabels>
      {showLabels && (
        <Labels ref={containerRef} count={visibleLabels.length}>
          {visibleLabels.map(({ label, posPct, labelName }) => {
            const originalIndex = labelsAndPositions.findIndex((item) => item.label === label);

            return (
              <LabelText
                key={label}
                style={{ [isRTL ? 'right' : 'left']: `${posPct}%` }}
                $isActive={originalIndex === achievedStep}
              >
                <NamedContentEntryText refKey={labelName} container={'span'} subKey="levelName" />
              </LabelText>
            );
          })}
        </Labels>
      )}
      <Meter value={progressPercentage} />
    </Container>
  ) : null;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const MeasureLabels = styled.div`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  height: 0;
  overflow: hidden;
`;

const MeasureLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 400;
  letter-spacing: 0.24px;
  margin-right: 24px;
`;

const Labels = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'count',
})<{ count: number }>`
  width: 100%;
  position: relative;
  height: 20px;
`;

const LabelText = styled.span<{ $isActive: boolean }>`
  white-space: nowrap;
  position: absolute;
  display: block;
  transform: translateX(-50%);
  color: ${({ $isActive, theme }) => ($isActive ? theme.colors.strong : theme.colors.normal)};
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
  font-size: 11px;
  font-style: normal;
  letter-spacing: 0.02em;
  line-height: 1.2;

  &:first-child {
    transform: translateX(0);
  }
  &:last-child {
    transform: translateX(-100%);
  }

  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSizes.medium};
  }
`;

const Meter = styled.div<{ value: number }>`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.tableBorder};
  height: 8px;
  position: relative;
  border-radius: 999px;

  &::before {
    content: ' ';
    height: 100%;
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.primary};
    width: ${({ value }) => value}%;
    position: absolute;
    transition: width 0.3s ease;
  }
`;
